import React from 'react';
import { Assert } from '@cotera/utilities';
import { Icon } from '@cotera/client/app/components/ui';
import { Loading } from '@cotera/client/app/components';
import { classNames } from '@cotera/client/app/components/utils';
import { Progress } from '@cotera/client/v0/components/ui/progress';
import { Button } from '@cotera/client/v0/components/ui/button';
import type { DatasetImportView } from './dataset-import';

/**
 * A CSV import, shown on the dataset it is filling.
 *
 * This used to be a modal over the page that started the upload, which could
 * only ever be right for one tab and only until it navigated. The import
 * outlives the request now, so it belongs to the dataset rather than to
 * whoever kicked it off: anyone opening the page mid-import sees it, and a
 * reload picks it back up from the persisted status.
 */

/**
 * The phases, in order, with the one currently running marked. A warehouse seed
 * has no browser upload — the server runs the query — so its first step is the
 * query rather than a file transfer.
 */
const STEPS_BY_VARIANT = {
  csv: [
    { key: 'uploading', label: 'Uploading file' },
    { key: 'parsing', label: 'Reading rows' },
    { key: 'indexing', label: 'Indexing rows' },
  ],
  warehouse: [
    { key: 'parsing', label: 'Running query' },
    { key: 'indexing', label: 'Indexing rows' },
  ],
} as const satisfies Record<string, readonly { key: string; label: string }[]>;

export type DatasetImportVariant = keyof typeof STEPS_BY_VARIANT;

type StepKey = 'uploading' | 'parsing' | 'indexing';

/**
 * Which step a phase is sitting in.
 *
 * `staged` folds into `parsing`: it means the bytes are down and the saga has
 * the work, which is the same thing to a reader as "reading rows" and would
 * otherwise be a step that flashes past too fast to register.
 */
const stepForPhase = (phase: DatasetImportView['phase']): StepKey | null => {
  switch (phase) {
    case 'uploading':
      return 'uploading';
    case 'staged':
    case 'parsing':
      return 'parsing';
    case 'indexing':
      return 'indexing';
    case 'failed':
      return null;
    default:
      return Assert.unreachable(phase);
  }
};

export const DatasetImportPanel: React.FC<{
  view: DatasetImportView;
  /** Which import this is, deciding the step list and labels. */
  variant?: DatasetImportVariant;
  /** Offered on failure, when there is something the user can do about it. */
  onRetry?: () => void;
}> = ({ view, variant = 'csv', onRetry }) => {
  const steps = STEPS_BY_VARIANT[variant];
  if (view.phase === 'failed') {
    return (
      <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
        <Icon
          icon="exclamation-triangle"
          className="mt-0.5 shrink-0 text-destructive"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-sm font-medium text-destructive">
            {variant === 'warehouse' ? 'Query failed' : 'Import failed'}
          </span>
          <span className="text-xs text-destructive/90">
            {view.error ??
              (variant === 'warehouse'
                ? 'The query could not be run against the warehouse.'
                : 'The CSV could not be read.')}
          </span>
          <span className="text-xs text-muted-foreground">
            {variant === 'warehouse'
              ? 'No rows were written. Check the SELECT statement and your warehouse connection, then try again.'
              : 'The dataset still holds whatever rows it had before this import.'}
          </span>
        </div>
        {onRetry === undefined ? null : (
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            <Icon icon="arrow-up-tray" size="small" />
            Try another file
          </Button>
        )}
      </div>
    );
  }

  const active = stepForPhase(view.phase);
  const uploading = view.phase === 'uploading';
  const uploadPct = uploading ? Math.round(view.fraction * 100) : 100;
  const activeIndex = steps.findIndex((step) => step.key === active);

  return (
    <div className="flex flex-col gap-4 rounded-md border bg-muted/30 px-4 py-3">
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-foreground">
            {uploading ? 'Uploading your file' : 'Processing on server'}
          </span>
          {uploading ? (
            <span className="text-lg font-semibold tabular-nums">
              {uploadPct}%
            </span>
          ) : (
            <Loading.Spinner variant="sm" />
          )}
        </div>
        {/* Only the upload has a real fraction. After it the server is reading
            and there is no signal, so the bar pulses at full rather than
            inventing a number. */}
        <Progress
          value={uploadPct}
          className={uploading ? undefined : 'animate-pulse'}
        />
      </div>

      <div className="flex flex-col gap-2">
        {steps.map((step, index) => (
          <StepRow
            key={step.key}
            label={step.label}
            state={
              index < activeIndex
                ? 'done'
                : index === activeIndex
                ? 'active'
                : 'pending'
            }
          />
        ))}
      </div>

      <span className="text-xs text-muted-foreground">
        Large files can take a moment. You can leave this page — the import
        keeps running and the rows appear when it finishes.
      </span>
    </div>
  );
};

type StepState = 'done' | 'active' | 'pending';

const StepRow: React.FC<{ label: string; state: StepState }> = ({
  label,
  state,
}) => (
  <div className="flex items-center gap-2">
    <StepIndicator state={state} />
    <span
      className={classNames(
        'text-sm',
        state === 'pending' ? 'text-muted-foreground' : 'text-foreground',
        state === 'active' && 'font-medium'
      )}
    >
      {label}
    </span>
  </div>
);

const StepIndicator: React.FC<{ state: StepState }> = ({ state }) => {
  switch (state) {
    case 'done':
      return <Icon icon="check" size="small" className="text-green-600" />;
    case 'active':
      return <Loading.Spinner variant="sm" />;
    case 'pending':
      return (
        <span className="h-3 w-3 rounded-full border border-muted-foreground/40" />
      );
    default:
      return Assert.unreachable(state);
  }
};
